import React, { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";

const createRow = () => ({
  id: Date.now() + Math.random(),
  dbId: null,
  left: "",
  right: "",
});

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");
  const year = parsed.getFullYear();
  const month = pad(parsed.getMonth() + 1);
  const day = pad(parsed.getDate());
  const hours = pad(parsed.getHours());
  const minutes = pad(parsed.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const TempleProgramSchedule = () => {
  const axiosAPI = useAxios();
  const [rows, setRows] = useState([createRow()]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleCellChange = (rowId, side, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [side]: value } : row)),
    );
  };

  const handleAddMore = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const handleRemoveRow = (rowId) => {

    console.log("Removing row with ID:", rowId);
    setRows((prev) => {
      if (prev.length === 1) {
        return prev;
      }
      return prev.filter((row) => row.id !== rowId);
    });
  };

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const res = await axiosAPI.get("/api/temple-program-schedule/get-active");
      const list = res?.data?.results || res?.data?.data || [];

      if (Array.isArray(list) && list.length > 0) {
        setIsUpdateMode(true);
        setRows(
          list
            .sort((a, b) => (a.row_no || 0) - (b.row_no || 0))
            .map((item) => ({
              id: Date.now() + Math.random(),
              dbId: item.id ?? null,
              left: item.left_text || item.left || "",
              right: toDateTimeLocalValue(item.right_datetime || item.right),
            })),
        );
      } else {
        setIsUpdateMode(false);
        setRows([createRow()]);
      }
    } catch (error) {
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage(
          "Error",
          "Failed to fetch temple program schedule.",
          "error",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedule();
  }, []);

  const validateRows = () => {
    for (let row of rows) {
      if (!row.left.trim() || !row.right.trim()) {
        return false;
      }
      if (isNaN(Date.parse(row.right))) {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateRows()) {
      window.notifyMessage(
        "Error",
        "Please fill all fields correctly. Right column should be a valid date-time.",
        "error",
      );
      return;
    }

    const payload = {
      rows: rows.map(({ dbId, left, right }, index) => ({
        ...(dbId !== null && { id: dbId }),
        row_no: index + 1,
        left_text: left.trim(),
        right_datetime: right,
      })),
    };

    // console.log("Submitting payload:", payload);

    try {
      setSubmitting(true);
      const response = isUpdateMode
        ? await axiosAPI.put("/api/temple-program-schedule/update", payload)
        : await axiosAPI.post("/api/temple-program-schedule/save", payload);
      const message =
        response?.data?.message ||
        (isUpdateMode
          ? "Schedule updated successfully."
          : "Schedule submitted successfully.");

      if (typeof window.notifyMessage === "function") {
        window.notifyMessage("Success", message, "success");
      }
      fetchSchedule();
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to submit schedule.";
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage("Error", message, "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <div className="page-inner">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h4 className="card-title mb-1">Temple Program Schedule</h4>
              <p className="mb-0 text-muted">
                You can directly edit both columns from the table below.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-dark btn-round"
              onClick={handleAddMore}
            >
              + Add More
            </button>
          </div>

          <div className="card-body">
            {loading && (
              <div className="text-center mb-3">
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>#</th>
                    <th>Left Column</th>
                    <th>Right Column</th>
                    <th style={{ width: 90 }}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{index + 1}</td>

                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Left side content"
                          value={row.left}
                          onChange={(event) =>
                            handleCellChange(row.id, "left", event.target.value)
                          }
                          disabled={submitting}
                        />
                      </td>

                      <td>
                        <input
                          type="datetime-local"
                          className="form-control form-control-sm"
                          placeholder="Right side content"
                          value={row.right}
                          onChange={(event) =>
                            handleCellChange(
                              row.id,
                              "right",
                              event.target.value,
                            )
                          }
                          disabled={submitting}
                        />
                      </td>

                      <td>
                        <button
                          type="button"
                          className="btn btn-icon btn-round btn-danger btn-sm"
                          disabled={rows.length === 1 || submitting}
                          onClick={() => handleRemoveRow(row.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card-footer bg-white">
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-primary btn-round"
                onClick={handleSubmit}
                disabled={submitting || loading}
              >
                {submitting
                  ? isUpdateMode
                    ? "Updating..."
                    : "Submitting..."
                  : isUpdateMode
                    ? "Update"
                    : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TempleProgramSchedule;
